/* eslint-disable max-params */
import { format, isToday, isSameDay } from 'date-fns';
import { formatPlates, formatPlatesPayload } from './genericHelpers';
import { convertApiDate, getDateString, getTimeString } from './dateHelpers';
import { getLabelValue } from './contentfulHelpers';
import {
	ICartItem,
	ICountry,
	ICustomerCountry,
	IDate,
	IDates,
	IOffer,
	IPriceComponent,
	IPricingInformation,
	ITollSectionOffer,
	ITotalPrice,
	IVehicleCategory,
	IVignetteData,
	IVignetteOffer,
	IPrice,
	ILabel,
	IDefaultPricing,
} from '../models';
import { ITypeVignettenCheckoutFields } from '../contentful-types';

/** Common Offer Helpers */

export const getProductByOfferHash = (offerHash: string, allOffers: IOffer[]) =>
	allOffers.find((offer) => offerHash === offer.offerHash);

export const getPreauthOfferHash = (isDKVPage: boolean): string => {
	const offerHash = sessionStorage.getItem('offerHash') ?? '';
	if (!isDKVPage) {
		return offerHash;
	}
	const memberOfferHash = sessionStorage.getItem('memberOfferHash') ?? '';
	const isMember = sessionStorage.getItem('isMember');
	return Boolean(isMember) ? memberOfferHash : offerHash;
};

// Business logic
// ModuleTypeId 1 is Mautbox and 2 is for Vignettes
// FuelTypeId -1 is for all types of fuel
export const getOffersByModuleAndMembership = (
	allOffers: IOffer[],
	isMember: boolean,
	currentModule: number,
): IVignetteOffer[] => {
	const today = new Date();
	if (isMember) {
		return allOffers.filter(
			({ moduleTypeId, memberYes, sellFrom, sellTo }) =>
				moduleTypeId === currentModule &&
				memberYes &&
				convertApiDate(sellFrom).getTime() <= today.getTime() &&
				convertApiDate(sellTo).getTime() >= today.getTime(),
		);
	}
	return allOffers.filter(
		({ moduleTypeId, memberNo, sellFrom, sellTo }) =>
			moduleTypeId === currentModule &&
			memberNo &&
			convertApiDate(sellFrom).getTime() <= today.getTime() &&
			convertApiDate(sellTo).getTime() >= today.getTime(),
	);
};

export const getCountriesByCustomer = (allCountries: ICountry[], customerCountries: ICustomerCountry[]) =>
	allCountries.filter((country: ICountry) => customerCountries.find((c: ICustomerCountry) => c.id === country.id));

/** Vignette Offer Helpers */

export const getVignetteByCatalogAndFuel = (vignetteOffers: IVignetteOffer[], catalogId: string, fuelId: number) =>
	vignetteOffers.find(
		({ serviceCatalogCode, fuelTypeId }) => serviceCatalogCode === catalogId && fuelTypeId === fuelId,
	);

export const getPriceAmountByCatalogId = (priceComponents: IPriceComponent[]): string => {
	const priceComponent = priceComponents.find(({ serviceItemId }) => serviceItemId === 5);
	return priceComponent ? priceComponent.amount.amount : '-';
};

export const getDecimalPriceAmountByCatalogId = (priceComponents: IPriceComponent[]): number => {
	const priceComponent = priceComponents.find(({ serviceItemId }) => serviceItemId === 5);
	return priceComponent ? priceComponent.amount.decimalAmount : 0;
};

export const getCorrespondingTransitionVignette = (
	isTransitionPeriod: boolean,
	vignetteOffers: IVignetteOffer[],
	vignette?: IVignetteOffer,
): IVignetteOffer | undefined => {
	if (!isTransitionPeriod) return vignette;
	return vignetteOffers.find(
		(offer) =>
			offer.serviceCatalogCode === vignette?.serviceCatalogCode &&
			offer.servcatSeason !== vignette?.servcatSeason,
	);
};

// Business logic
// Check-out date is considered as start date
// Check-in  date is considered as end date
export const getVignetteDates = (
	dates?: IDates,
	variationCode?: string,
): { checkInDateTime?: IDate; checkOutDateTime: IDate } => {
	const checkingCheckoutPayload = {
		checkOutDateTime: {
			date: getDateString(dates ? dates?.start : new Date()),
			time: getTimeString(dates ? dates?.start : new Date()),
		},
		checkInDateTime: {
			date: getDateString(dates ? dates?.end : new Date()),
			time: getTimeString(dates ? dates?.end : new Date()),
		},
	};

	const onlyCheckOutPayload = {
		checkOutDateTime: {
			date: getDateString(dates ? dates?.start : new Date()),
			time: getTimeString(dates ? dates?.start : new Date()),
		},
	};

	return variationCode && variationCode === 'FLEX' ? onlyCheckOutPayload : checkingCheckoutPayload;
};

export const getVignetteData = (selectedVignette: IVignetteOffer, asPayload = true): IVignetteData => {
	return {
		licenseplate: asPayload ? formatPlatesPayload(selectedVignette.plates) : formatPlates(selectedVignette.plates),
		...getVignetteDates(selectedVignette.dates, selectedVignette.variationCode),
		countryCode: selectedVignette.countryCode ?? '',
		licenseplateType: selectedVignette.licenseplateType ?? 0,
	};
};

// Business logic
// ModuleDetails are for us Vignette categories and/or Vehicle types (e.g. Motorrad, PKW, Transporter)
export const getVignetteUniqueCategories = (vignetteOffers: IVignetteOffer[]): IVehicleCategory[] =>
	vignetteOffers
		.map(({ moduleDetailId, moduleDetail, tollClass }) =>
			tollClass
				? { id: moduleDetailId, name: moduleDetail, tollClass }
				: { id: moduleDetailId, name: moduleDetail },
		)
		.reduce((acc: IVehicleCategory[], value: IVehicleCategory) => {
			const { id } = value;
			const idExists = acc.find((a: any) => a?.id === id);
			return idExists ? acc : [...acc, value];
		}, []);

// Business logic
// Check-out date is considered as start date
// Check-in  date is considered as end date
export const convertVignetteIDateToDates = (checkInDateTime: IDate, checkOutDateTime: IDate): IDates => ({
	end: convertApiDate(checkInDateTime),
	start: convertApiDate(checkOutDateTime),
});

// sofort shouldn't be rendered when checkInDateTime and checkOutDateTime are equal
// e.g. for 1 Day Vignettes
export const getGermanDateFormat = (date: Date, withTime = false, lang = 'de', dateRangesAreEqual = false, labels?) => {
	if (isToday(date) && !withTime && !dateRangesAreEqual) {
		return getLabelValue('mp.immediately', labels) || 'Sofort';
	}

	return `${format(date, withTime ? `dd.MM.yyyy ${lang === 'en' ? 'hh:mm a' : 'HH:mm'}` : 'dd.MM.yyyy')}${
		withTime && lang === 'de' ? ' Uhr' : ''
	}`;
};

// Business logic
// Check-out date is considered as start date
// Check-in  date is considered as end date
export const formatVignetteDatesRange = (
	checkInDateTime: Date,
	checkOutDateTime: Date,
	withTime = false,
	lang = 'de',
	labels?: ILabel[],
): string => {
	const dateRangesAreEqual = isSameDay(checkInDateTime, checkOutDateTime);
	const checkInDateGermanFormat = getGermanDateFormat(checkInDateTime, withTime, lang, dateRangesAreEqual, labels);
	const checkOutDateGermanFormat = getGermanDateFormat(checkOutDateTime, withTime, lang, false, labels);

	return `${checkOutDateGermanFormat} – ${checkInDateGermanFormat}`;
};

/** Dkv Offers Helpers */

// Business logic
// ModuleTypeId 1 is Mautbox
// FuelTypeId -1 is for all types of fuel
export const getDkvOfferHashByModuleAndMembership = (
	dkvOffers: IOffer[] = [],
	isMember: boolean,
	currentFuel = -1,
): string => {
	if (isMember) {
		return dkvOffers.find(({ memberYes, fuelTypeId }) => memberYes && fuelTypeId === currentFuel)?.offerHash ?? '';
	}
	return dkvOffers.find(({ memberNo, fuelTypeId }) => memberNo && fuelTypeId === currentFuel)?.offerHash ?? '';
};

// get corresponding of member/ non member Price for non member / member offer
export const getCorrespondingOffer = (allOffers: IOffer[], offer?: IOffer) => {
	return allOffers.find(
		({ fuelTypeId, memberYes, moduleTypeId, serviceCatalogName }) =>
			fuelTypeId === offer?.fuelTypeId &&
			!offer.memberYes === memberYes &&
			moduleTypeId === offer?.moduleTypeId &&
			serviceCatalogName === offer?.serviceCatalogName,
	);
};

export const formatTripsAmountLabel = (trips: number | undefined, labels: ILabel[] | undefined) => {
	if (trips)
		return `${trips} ${
			trips > 1
				? getLabelValue('mp.multipleTrips', labels) || 'Fahrten'
				: getLabelValue('mp.singleTrip', labels) || 'Fahrt'
		}`;
	return '';
};

export const renderSubLabel = (offer: IOffer, labels: ILabel[] | undefined, cartLabels) => {
	switch (offer.moduleTypeId) {
		case 2:
			return offer.moduleDetail;
		case 3:
			return `${formatTripsAmountLabel(offer.trips, labels)}${offer.sectionName ? `, ${offer.sectionName}` : ''}`;
		case 4:
			return cartLabels?.allTollRoutesUsable;
		default:
			return '';
	}
};

export const getTotalPrice = (selectedOffer, label, withAdditionalInfo = true): IDefaultPricing => {
	const extractedTotalPrice = selectedOffer?.prices.find((price) => price.type === 'total')?.amount;
	return {
		label,
		amount: extractedTotalPrice ? `${extractedTotalPrice?.amount}€` : undefined,
		decimalAmount: Number(extractedTotalPrice?.decimalAmount),
		serviceFee: selectedOffer?.priceComponents.find((price) => price.type === 'oneoff')?.amount.decimalAmount,
		additionalInfo: withAdditionalInfo ? 'zzgl. Service- & Mautgebühren bei Nutzung' : undefined,
	};
};

export const getTotalServiceFee = (
	sumDiscount: IPrice | undefined,
	sumServiceFee: IPrice | undefined,
	hasMembership: boolean,
) => {
	const totalDiscount = Number(sumDiscount?.decimalAmount);
	return hasMembership
		? `${(Number(sumServiceFee?.decimalAmount) + totalDiscount).toFixed(2)}€`
		: `${sumServiceFee?.amount}€`;
};

export const getServiceFeePerProduct = (
	sumDiscount: IPrice | undefined,
	sumServiceFee: IPrice | undefined,
	numberOfProducts: number,
	hasMembership: boolean,
) => {
	const discountPerProduct = Number(sumDiscount?.decimalAmount) / numberOfProducts;
	return `${(Number(sumServiceFee?.decimalAmount) / numberOfProducts + (hasMembership ? discountPerProduct : 0))
		.toFixed(2)
		.replace('.', ',')}€`;
};

export const getDiscount = (correspondingTotalPrice: IPrice, totalPrice: IPrice) =>
	String(Math.abs(correspondingTotalPrice.decimalAmount - totalPrice.decimalAmount).toFixed(2)).replace('.', ',');

export const getPricingInformation = (
	selectedOffer: IOffer,
	labels: ILabel[] | undefined,
	contentfulLabels: ITypeVignettenCheckoutFields,
	correspondingOffer?: IOffer,
	hasMembership?: boolean,
): IPricingInformation[] => {
	const isFlexService = selectedOffer.moduleTypeId === 4;
	const subLabel = renderSubLabel(selectedOffer, labels, contentfulLabels?.cart);

	const pricings = selectedOffer?.priceComponents.map((price) => {
		const correspondingPricing = correspondingOffer?.priceComponents.find(
			(comp) => comp.serviceItemId === price.serviceItemId,
		);
		const showDiscount = price.serviceItemId === 8 && hasMembership && correspondingPricing;
		return {
			label: price.serviceItemId === 8 ? price.serviceItemName : selectedOffer.serviceCatalogName,
			value: isFlexService ? contentfulLabels?.priceDetails?.payLater : `${price?.amount?.amount}€`,
			isProductPrice: price.serviceItemId === 5 || isFlexService,
			subLabel: price.serviceItemId === 8 ? undefined : subLabel,
			previousValue: showDiscount ? `${correspondingPricing?.amount?.amount}€` : undefined,
			discountLabel: showDiscount
				? `${getDiscount(correspondingPricing?.amount, price.amount)}€ Mitgliederrabatt`
				: undefined,
		};
	});
	return pricings;
};

export const updateFuelType = (
	updateSelectedCartItem: (cartItem: ICartItem) => void,
	vignetteOffers: IOffer[],
	fuelTypeId?: number,
	selectedCartItem?: ICartItem,
) => {
	const updatedSelectedCartItem = vignetteOffers.find(
		(v) => v.serviceCatalogCode === selectedCartItem?.offer.serviceCatalogCode && v.fuelTypeId === fuelTypeId,
	);
	if (updatedSelectedCartItem) {
		const updatedCartItem = {
			...selectedCartItem,
			offerHash: updatedSelectedCartItem.offerHash,
			offer: updatedSelectedCartItem,
		};
		updateSelectedCartItem(updatedCartItem as ICartItem);
		return updatedCartItem;
	}
	return undefined;
};

export const getCheapestTollSection = (tollRoutes: ITollSectionOffer[] | []): ITollSectionOffer[] | undefined => {
	if (tollRoutes.length === 0) {
		return undefined;
	}
	const flexTollSestion = tollRoutes.find(({ moduleTypeId }) => moduleTypeId === 4);
	const tollSections = tollRoutes.filter(({ moduleTypeId }) => moduleTypeId === 3);
	const cheapestTollSection =
		tollSections.length > 0
			? [
					tollSections.reduce((acc, curr) => {
						const [accTotal] = acc.prices;
						const [currTotal] = curr.prices;
						return accTotal.amount.decimalAmount < currTotal.amount.decimalAmount ? acc : curr;
					}),
			  ]
			: [];
	cheapestTollSection.map((el) => ({
		...el,
		serviceCatalogName: 'Digitale Streckenmaut',
		serviceCatalogCode: 'Digitale Streckenmaut',
		moduleType: flexTollSestion ? 'Einzelfahrkarte' : el.moduleType,
	}));
	return flexTollSestion
		? [flexTollSestion, ...cheapestTollSection].sort((a, b) => a.moduleTypeId - b.moduleTypeId)
		: cheapestTollSection;
};

export const getExistingUniqueVehicleData = (cartItems: ICartItem[], isOneCartFlow: boolean): IVehicleCategory[] => {
	const formatUniqueVehicleCategories = (cart: ICartItem[]): IVehicleCategory[] =>
		cart
			.reduce(
				(acc: ICartItem[], curr: ICartItem) =>
					acc.find(
						(currItem) =>
							currItem.licenseplate === curr.licenseplate &&
							currItem.offer.moduleDetailId === curr.offer.moduleDetailId,
					)
						? acc
						: [...acc, curr],
				[],
			)
			.map(({ licenseplate, licenseplateType, offer, countryCode }) => ({
				licenseplate,
				licenseplateType,
				countryCode,
				name: offer.moduleDetail,
				id: offer.moduleDetailId,
			}));
	if (isOneCartFlow) {
		const filteredCartItems = cartItems.filter((el, _, cart) => {
			if (el.offer.moduleDetailId === 2) {
				const hasTransporterWithSamePlates = cart.find(
					(prod) => prod.offer.moduleDetailId === 4 && prod.licenseplate === el.licenseplate,
				);
				if (hasTransporterWithSamePlates) return false;
			}
			return el;
		});
		return formatUniqueVehicleCategories(filteredCartItems);
	}
	return formatUniqueVehicleCategories(cartItems);
};

export const getCartTotalPrice = (
	cartPrice: IPrice | undefined,
	currentLabels: ITypeVignettenCheckoutFields,
	onlyFlexProduct?: boolean,
): ITotalPrice => {
	let totalPriceLabel = currentLabels?.priceDetails?.totalPriceHeadline || 'Gesamtpreis';
	if (onlyFlexProduct) {
		totalPriceLabel = currentLabels?.priceDetails?.payNowHeadline || 'Zu zahlen';
	}
	return {
		amount: cartPrice ? `${cartPrice.amount}€` : undefined,
		decimalAmount: Number(cartPrice?.decimalAmount),
		label: totalPriceLabel,
	};
};

export const preSelectFirstMatchingVignette = (
	products: IVignetteOffer[],
	preselectedMatchCodes: string[],
	vehicleCategory: IVehicleCategory | undefined,
): (IVignetteOffer | undefined)[] =>
	preselectedMatchCodes
		.map((matchCode) => {
			// Adding Toll Class dynamically from slovenian products
			if (matchCode?.includes('-') && vehicleCategory) {
				const productByCategory = products.find((p) => p.moduleDetailId === vehicleCategory.id);
				matchCode = matchCode?.replace('-', productByCategory?.tollClass ?? '');
			}
			return products.find((vignette) => vignette.matchCode.startsWith(matchCode || ''));
		})
		.filter((vignette) => vignette !== undefined);

export const preSelectSectionTolls = (
	products: ITollSectionOffer[],
	preselectedMatchCodes: string[],
): (ITollSectionOffer | undefined)[] =>
	preselectedMatchCodes
		.map((matchCode) => products.find((vignette) => vignette.matchCode === matchCode))
		.filter((sectionToll) => sectionToll !== undefined);
